/**
 * makePhysicsBacking — adapts a runtime's GUID-addressed physics subsystem to the
 * entity-addressed {@link PhysicsBacking} that `engine.physics` consumes.
 *
 * Both the player and editor `PhysicsSubsystem` expose a GUID-keyed query/force surface
 * ({@link GuidPhysicsApi}); scripts speak {@link EntityHandle}. This is the DI seam that maps
 * between them per runtime (entity↔GUID via the live Level), resolving the subsystem lazily so
 * it tolerates physics not-yet-initialized / absent (play-mode lifecycle).
 *
 * Design ref: `.refactor/SCRIPTING-API-DESIGN.md` §13 step 4c (per-runtime DI backing).
 */

import type { EntityHandle, Vec2Like, RaycastHit } from "./IEngineContext";
import { NO_ENTITY } from "./IEngineContext";
import type { PhysicsBacking } from "./modules/PhysicsScriptApi";

/** A GUID-addressed raycast result (the shape both PhysicsSubsystems already return). */
export interface GuidRaycastHit {
  point: Vec2Like;
  normal: Vec2Like;
  distance: number;
  bodyGuid?: string;
}

/**
 * The GUID-addressed physics surface both runtimes' `PhysicsSubsystem` implement structurally
 * (player gains it in 4c; editor already has it).
 */
export interface GuidPhysicsApi {
  raycast(
    ox: number,
    oy: number,
    dx: number,
    dy: number,
    maxDist: number,
    layerMask?: number,
  ): GuidRaycastHit | null;
  overlapCircle(cx: number, cy: number, radius: number, layerMask?: number): string[];
  applyForce(guid: string, fx: number, fy: number): void;
  applyImpulse(guid: string, ix: number, iy: number): void;
  setLinearVelocity(guid: string, vx: number, vy: number): void;
  getLinearVelocity(guid: string): { x: number; y: number };
}

const ZERO: Vec2Like = { x: 0, y: 0 };

/**
 * Build a {@link PhysicsBacking} over a (lazily-resolved) {@link GuidPhysicsApi} and the
 * entity↔GUID resolvers. Every method no-ops gracefully when the subsystem is absent.
 *
 * @param getApi      resolves the live physics subsystem, or `null` if unavailable
 * @param entityToGuid maps a script entity handle to its GameObject GUID (or `null`)
 * @param guidToEntity maps a physics body GUID back to an entity handle (or `null`)
 */
export function makePhysicsBacking(
  getApi: () => GuidPhysicsApi | null,
  entityToGuid: (entity: EntityHandle) => string | null,
  guidToEntity: (guid: string) => EntityHandle | null,
): PhysicsBacking {
  return {
    raycast: (origin, direction, maxDistance): RaycastHit | null => {
      const api = getApi();
      if (!api) return null;
      const hit = api.raycast(
        origin.x,
        origin.y,
        direction.x,
        direction.y,
        maxDistance ?? Infinity,
      );
      if (!hit) return null;
      return {
        entity: hit.bodyGuid ? guidToEntity(hit.bodyGuid) ?? NO_ENTITY : NO_ENTITY,
        point: hit.point,
        normal: hit.normal,
        distance: hit.distance,
      };
    },

    overlapCircle: (center, radius): readonly EntityHandle[] => {
      const api = getApi();
      if (!api) return [];
      return api
        .overlapCircle(center.x, center.y, radius)
        .map(guidToEntity)
        .filter((e): e is EntityHandle => e != null);
    },

    applyForce: (entity, fx, fy): void => {
      const api = getApi();
      const guid = entityToGuid(entity);
      if (api && guid) api.applyForce(guid, fx, fy);
    },

    applyImpulse: (entity, ix, iy): void => {
      const api = getApi();
      const guid = entityToGuid(entity);
      if (api && guid) api.applyImpulse(guid, ix, iy);
    },

    setVelocity: (entity, vx, vy): void => {
      const api = getApi();
      const guid = entityToGuid(entity);
      if (api && guid) api.setLinearVelocity(guid, vx, vy);
    },

    getVelocity: (entity): Vec2Like => {
      const api = getApi();
      const guid = entityToGuid(entity);
      return api && guid ? api.getLinearVelocity(guid) : ZERO;
    },
  };
}
